/**
 * Shared Kanban board component used by both Returns and Service modules.
 * - statuses: [{id, name, color}]
 * - items: [{id, status_id, ...}]
 * - renderCard: (item) => ReactNode
 * - onMove: (itemId, fromStatusId, toStatusId) => Promise<void>  (optimistic)
 */
import { useMemo, useState } from "react";
import {
  DndContext, PointerSensor, useSensor, useSensors, closestCorners, DragOverlay,
} from "@dnd-kit/core";
import { useDroppable, useDraggable } from "@dnd-kit/core";

function Card({ id, children }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id });
  const style = transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`, opacity: isDragging ? 0.4 : 1 } : {};
  return (
    <div ref={setNodeRef} style={style} {...listeners} {...attributes} className="cursor-grab active:cursor-grabbing">
      {children}
    </div>
  );
}

function Column({ status, items, renderCard }) {
  const { setNodeRef, isOver } = useDroppable({ id: status.id });
  return (
    <div className="w-80 shrink-0" data-testid={`kanban-col-${status.name}`}>
      <div className="flex items-center justify-between px-2 mb-3">
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full" style={{ background: status.color }} />
          <span className="font-semibold text-sm">{status.name}</span>
          <span className="text-xs text-muted-foreground font-mono">{items.length}</span>
        </div>
      </div>
      <div
        ref={setNodeRef}
        className={`bg-muted/30 rounded-lg p-2 min-h-[400px] space-y-2 transition-colors ${isOver ? "bg-primary/10 ring-2 ring-primary/30" : ""}`}
      >
        {items.length === 0 && <div className="text-xs text-muted-foreground/60 text-center py-8">Goală</div>}
        {items.map((it) => (
          <Card key={it.id} id={it.id}>{renderCard(it)}</Card>
        ))}
      </div>
    </div>
  );
}

export function KanbanBoard({ statuses, items, renderCard, onMove }) {
  const [activeId, setActiveId] = useState(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const byStatus = useMemo(() => {
    const out = {};
    statuses.forEach((s) => (out[s.id] = []));
    items.forEach((it) => {
      if (!out[it.status_id]) out[it.status_id] = [];
      out[it.status_id].push(it);
    });
    return out;
  }, [items, statuses]);

  const active = items.find((it) => it.id === activeId);

  const handleDragEnd = async (e) => {
    setActiveId(null);
    const { active, over } = e;
    if (!over || !active) return;
    const item = items.find((it) => it.id === active.id);
    if (!item || item.status_id === over.id) return;
    onMove(item.id, item.status_id, over.id);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={(e) => setActiveId(e.active.id)}
      onDragEnd={handleDragEnd}
      onDragCancel={() => setActiveId(null)}
    >
      <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-thin -mx-4 px-4 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
        {statuses.map((s) => (
          <Column key={s.id} status={s} items={byStatus[s.id] || []} renderCard={renderCard} />
        ))}
      </div>
      <DragOverlay>{active ? renderCard(active) : null}</DragOverlay>
    </DndContext>
  );
}
